import type { EmailOrigin } from '@fluenceur/types';
import { useAuth } from '@/contexts/auth-context';
import { useUpdateContactDetails } from '@/hooks/actions/use-update-contact-details';
import { useContactDetailsQuery } from '@/hooks/queries/use-contact-details-query';
import { useResolveContactQuery } from '@/hooks/queries/use-resolve-contact-query';
import { useContactResolveRef } from '../use-contact-resolve-ref';
import { useResolvedContactIdentityId } from '../use-resolved-contact-identity-id';

export interface ActiveContactDetails {
  email: string;
  /**
   * Where the shown email came from, resolved server-side (`manual` when the
   * brand set an override, else `affiliate` / `enriched` / null). Drives the
   * discreet provenance caption; never re-derived on the client.
   */
  emailOrigin: EmailOrigin;
  phone: string;
  saveEmail: (value: string) => void;
  savePhone: (value: string) => void;
  isSaving: boolean;
  /**
   * True once a creator identity is resolved and edits actually persist. False
   * while the identity is unresolved (loading, or a creator the brand has not
   * saved) — callers render the fields read-only rather than editable-but-inert.
   */
  editable: boolean;
}

const EMPTY_FALLBACK: ActiveContactDetails = {
  email: '',
  emailOrigin: null,
  phone: '',
  saveEmail: () => {},
  savePhone: () => {},
  isSaving: false,
  editable: false,
};

/**
 * Resolves the active contact email/phone for the CONTACT rail section, keyed by
 * the resolved `creatorIdentityId`.
 *
 * Email + provenance are read straight from the server resolve query, which is
 * already the single source of truth: the backend collapses the brand override,
 * the affiliation login email, and the enrichment email into `primaryEmail` /
 * `emailOrigin`. The client does NOT re-apply precedence — doing so previously
 * made a cleared email reappear from the stale resolve fallback. The update
 * mutation invalidates that resolve entry on settle, so a clear/edit refetches
 * the collapsed truth.
 *
 * Phone has no resolved source, so it is the brand override or empty. When the
 * identity is unresolved, returns an empty slice so the section renders its
 * placeholders and edits are inert.
 *
 * While a save is in flight OR the resolve refetch it triggers is still landing,
 * the override cache (optimistically written by the mutation, then confirmed by
 * its response) is echoed instead. TanStack keeps the previous resolve data
 * visible during a background refetch, so without the second condition a cleared
 * email would flash back between the mutation settling and the refetch landing.
 * For that echo window the override IS the server truth for the manual path:
 * post-clear it is null (field empty), post-edit it is the new value.
 *
 * Saving trims the input and normalizes an empty string to `null` (a clear),
 * matching the server's own normalization.
 */
export const useActiveContactDetails = (): ActiveContactDetails => {
  const { user } = useAuth();
  const creatorIdentityId = useResolvedContactIdentityId();
  const resolveRef = useContactResolveRef();
  const { data: resolved, isFetching: resolveIsFetching } = useResolveContactQuery(resolveRef);
  const { data: override } = useContactDetailsQuery(creatorIdentityId);
  const { mutate, isPending } = useUpdateContactDetails(creatorIdentityId, user?.currentBrandId);

  if (!creatorIdentityId) {
    return EMPTY_FALLBACK;
  }

  // Server-resolved truth. Echo the override while a save is in flight AND while
  // the resolve refetch it triggered is still landing — the previous resolve data
  // stays visible during a background refetch and would flash the old email back.
  const echoOverride = isPending || resolveIsFetching;
  const email = echoOverride ? (override?.email ?? '') : (resolved?.primaryEmail ?? '');
  const emailOrigin: EmailOrigin = echoOverride
    ? override?.email
      ? 'manual'
      : null
    : (resolved?.emailOrigin ?? null);

  return {
    email,
    emailOrigin,
    phone: override?.phone ?? '',
    saveEmail: (value: string) => mutate({ email: value.trim() || null }),
    savePhone: (value: string) => mutate({ phone: value.trim() || null }),
    isSaving: isPending,
    editable: true,
  };
};
